import { c as _c } from "react/compiler-runtime";
import { Formik } from 'formik';
import * as yup from 'yup';
import classNames from 'classnames';
import Container from '@helios/Container';
import IconButton from '@helios/IconButton';
import Button from '@helios/Button';
import FormActions from '@helios/FormActions';
import Spinner from '@helios/Spinner';
import { CLASS_TIMES_SAVE_FAILED_MESSAGE, SHOW_FIELD_ERROR_TEXT } from '../fieldErrorMessages';
import logger from '@/utils/logger';
import { useEffect, useState } from 'react';
import { FormattedBrandLogo } from '../FormattedBrandLogo';
import { usePostHog } from 'posthog-js/react';
import { useMagicLinkRedirect } from '@/hooks/useMagicLinkRedirect';
import RedirectBuffer from '../RedirectBuffer';
import { captureAnalyticsEvent, captureLegacyAnalyticsEvent } from '@/lib/analytics';
import RegularHouseIcon from '@/components/icons/regular/house';
import ClassTimeSlots, { useDegreeAwareClassTimes } from './ClassTimeSlots.js';
import RedirectSlidePreload from './RedirectSlidePreload.js';
import { CLASS_TIME_SLOTS, CLASS_TIMES_REQUIRED_MESSAGE, EMPTY_CLASS_TIMES, buildClassTimesPayload, hasPreferredClassTime, splitClassTimeSlots } from './classTimes';
import styles from './ApplicationForm.module.scss';

/**
 * The class-time preference after the submit (control). A saved preference
 * hands the applicant off to the platform: the redirect buffer at once, which
 * redirects when the magic link arrives (fetched since this step mounted),
 * and the Submitted screen only when no link is coming.
 */
function SchedulePreference(t0) {
  const $ = _c(41);
  const {
    site,
    employer,
    onSubmitClassTimes,
    setStep,
    degreeId,
    degreeName,
    classTimesOffered,
    applicationId,
    programStepVariant: t1
  } = t0;
  const programStepVariant = t1 === undefined ? "control" : t1;
  const [noAnswer, setNoAnswer] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const slotsError = noAnswer ? CLASS_TIMES_REQUIRED_MESSAGE : saveFailed && SHOW_FIELD_ERROR_TEXT ? CLASS_TIMES_SAVE_FAILED_MESSAGE : undefined;
  const [handingOff, setHandingOff] = useState(false);
  const isDegreeAwareEnabled = useDegreeAwareClassTimes();
  const posthog = usePostHog();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const magicLinkQuery = useMagicLinkRedirect(t2);
  const noLinkComing = !!magicLinkQuery?.isError || !!magicLinkQuery?.isIdle;
  const [fellBack, setFellBack] = useState(false);
  if (handingOff && noLinkComing && !fellBack) {
    setFellBack(true);
  }
  let t3;
  let t4;
  if ($[1] !== fellBack || $[2] !== setStep) {
    t3 = () => {
      if (fellBack) {
        setStep(_temp);
      }
    };
    t4 = [fellBack, setStep];
    $[1] = fellBack;
    $[2] = setStep;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let optionalSlots;
  let preferredSlots;
  let t5;
  if ($[5] !== classTimesOffered || $[6] !== isDegreeAwareEnabled) {
    ({
      preferredSlots,
      optionalSlots
    } = splitClassTimeSlots(classTimesOffered, isDegreeAwareEnabled));
    t5 = yup.object().shape({
      morning: yup.boolean(),
      evening: yup.boolean(),
      night: yup.boolean()
    }).test("atLeastOne", CLASS_TIMES_REQUIRED_MESSAGE, value => hasPreferredClassTime(value, preferredSlots));
    $[5] = classTimesOffered;
    $[6] = isDegreeAwareEnabled;
    $[7] = optionalSlots;
    $[8] = preferredSlots;
    $[9] = t5;
  } else {
    optionalSlots = $[7];
    preferredSlots = $[8];
    t5 = $[9];
  }
  const validationSchema = t5;
  if (handingOff) {
    let t6;
    if ($[10] !== fellBack || $[11] !== magicLinkQuery?.data) {
      t6 = fellBack ? null : <RedirectBuffer redirectUrl={magicLinkQuery?.data} countdown={5} />;
      $[10] = fellBack;
      $[11] = magicLinkQuery?.data;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <RedirectSlidePreload />;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== preferredSlots) {
    t7 = values => {
      setNoAnswer(!hasPreferredClassTime(values, preferredSlots));
    };
    $[14] = preferredSlots;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== applicationId || $[17] !== degreeId || $[18] !== degreeName || $[19] !== isDegreeAwareEnabled || $[20] !== noAnswer || $[21] !== onSubmitClassTimes || $[22] !== optionalSlots || $[23] !== posthog || $[24] !== preferredSlots || $[25] !== programStepVariant || $[26] !== site) {
    t8 = async values_0 => {
      if (noAnswer) {
        return;
      }
      setSaveFailed(false);
      const {
        payload,
        preferred,
        optional
      } = buildClassTimesPayload(values_0, {
        preferredSlots,
        optionalSlots,
        degreeAware: isDegreeAwareEnabled
      });
      await onSubmitClassTimes(payload).then(() => {
        captureLegacyAnalyticsEvent(posthog, "Class Times Submitted", {
          applicationId,
          degreeId,
          degreeName,
          preferredSlotsSelected: CLASS_TIME_SLOTS.filter(slot => preferred[slot]).length,
          optionalSlotsSelected: CLASS_TIME_SLOTS.filter(slot_0 => optional[slot_0]).length,
          preferred,
          optional,
          flag_on: isDegreeAwareEnabled,
          source: "post_submit",
          program_step_variant: programStepVariant
        });
        setHandingOff(true);
      }).catch(err => {
        setSaveFailed(true);
        captureAnalyticsEvent(posthog, "application_flow_failed", {
          application_id: applicationId,
          site,
          stage: "schedule_preference",
          program_step_variant: programStepVariant
        });
        logger.error(err, {
          description: "Schedule preference submit failed",
          source: "SchedulePreference"
        });
      });
    };
    $[16] = applicationId;
    $[17] = degreeId;
    $[18] = degreeName;
    $[19] = isDegreeAwareEnabled;
    $[20] = noAnswer;
    $[21] = onSubmitClassTimes;
    $[22] = optionalSlots;
    $[23] = posthog;
    $[24] = preferredSlots;
    $[25] = programStepVariant;
    $[26] = site;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== degreeName || $[29] !== employer || $[30] !== isDegreeAwareEnabled || $[31] !== optionalSlots || $[32] !== preferredSlots || $[33] !== site || $[34] !== slotsError) {
    t9 = formik => <form onSubmit={formik.handleSubmit}><div className={styles["corner-actions"]}><IconButton icon={<RegularHouseIcon />} label="Home" href="/" /></div><Container width="sm" gutters="compact"><div className={styles.slots}><FormattedBrandLogo site={site} employer={employer} /><h1 className={classNames(styles["step-title"], styles["step-title--wide"])}>Preferred class times</h1><div className={styles.slots__panel}><ClassTimeSlots site={site} degreeName={degreeName} degreeAware={isDegreeAwareEnabled} preferredSlots={preferredSlots} optionalSlots={optionalSlots} error={slotsError} /><FormActions><Button type="submit" shape="rounded" fullWidth={true} disabled={formik.isSubmitting}>{formik.isSubmitting ? <Spinner label="Submitting" /> : "Submit"}</Button></FormActions></div></div></Container></form>;
    $[28] = degreeName;
    $[29] = employer;
    $[30] = isDegreeAwareEnabled;
    $[31] = optionalSlots;
    $[32] = preferredSlots;
    $[33] = site;
    $[34] = slotsError;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== t7 || $[37] !== t8 || $[38] !== t9 || $[39] !== validationSchema) {
    t10 = <>{t6}<Formik initialValues={EMPTY_CLASS_TIMES} validationSchema={validationSchema} validate={t7} onSubmit={t8}>{t9}</Formik></>;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = validationSchema;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
}
function _temp(s) {
  return s + 1;
}
export default SchedulePreference;